import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import { Link } from 'react-router-dom';
import i18next from 'i18next';
import { getMuiTablePaginationI18nProps } from 'components/table/muiTablePaginationI18nProps';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';

import {
  REPORT_HOTEL_CONTRACTS_LIST_QUERY,
  REPORT_HOTEL_CONTRACTS_LIST_COUNT_QUERY,
  REPORT_LIST_HOTEL_GROUPS_QUERY,
} from '../gql';
import { allCountriesSorted } from '../../../languages';
import { getSubdivisionsForCountry, getSubdivisionName } from 'semshared/utils/subdivisions';
import {
  CONTRACT_TYPE_LABELS,
  formatContractType,
  formatInvoiceInterval,
  centsToEuro,
  getInvoiceIntervalOptions,
} from './utils/contractHelpers';
import { ECountryCode, EReportsSortDirection } from '../../../__generated__/graphql';
import { SimpleDatePicker } from 'components/form/FormInputDate';
import { formatNumber, formatDate } from 'semshared/utils/format';

interface ContractFilters {
  country?: ECountryCode;
  state?: string;
  groupIds?: number[];
  agreementTypes?: string[];
  validFrom?: string;
  validTo?: string;
  invoiceInterval?: string;
  showInLister?: boolean;
}

export default function HotelContractsTab() {
  const [filters, setFilters] = useState<ContractFilters>({
    groupIds: [],
    agreementTypes: [],
    showInLister: true,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [loadContracts, { data: contractsData, loading: contractsLoading }] = useLazyQuery(
    REPORT_HOTEL_CONTRACTS_LIST_QUERY,
    { fetchPolicy: 'network-only' },
  );
  const [loadCount, { data: countData }] = useLazyQuery(REPORT_HOTEL_CONTRACTS_LIST_COUNT_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [loadGroups, { data: groupsData }] = useLazyQuery(REPORT_LIST_HOTEL_GROUPS_QUERY);

  // Hotel states from ISO 3166-2
  const states = React.useMemo(
    () => getSubdivisionsForCountry(filters.country || ''),
    [filters.country]
  );

  React.useEffect(() => {
    loadGroups();
  }, []);

  const handleFilterChange = (field: keyof ContractFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    if (field === 'country') {
      setFilters(prev => ({ ...prev, state: undefined }));
    }
  };

  const handleGenerateReport = () => {
    const queryFilters: any = {};
    if (filters.agreementTypes && filters.agreementTypes.length > 0) {
      queryFilters.agreementTypes = filters.agreementTypes;
    }
    if (filters.country) {
      queryFilters.country = filters.country;
    }
    if (filters.state) {
      queryFilters.state = filters.state;
    }
    if (filters.groupIds && filters.groupIds.length > 0) {
      queryFilters.groupIds = filters.groupIds;
    }
    if (filters.validFrom) {
      queryFilters.validFrom = filters.validFrom;
    }
    if (filters.validTo) {
      queryFilters.validTo = filters.validTo;
    }
    if (filters.invoiceInterval) {
      queryFilters.invoiceInterval = filters.invoiceInterval;
    }
    if (filters.showInLister !== undefined) {
      queryFilters.showInLister = filters.showInLister;
    }

    // Load all hotels (no pagination on backend)
    loadContracts({
      variables: {
        filters: queryFilters,
        sort: [{ hotelName: EReportsSortDirection.asc }],
      },
    });
    loadCount({ variables: { filters: queryFilters } });
    setPage(0);
  };

  const handleReset = () => {
    setFilters({
      groupIds: [],
      agreementTypes: [],
      showInLister: true,
    });
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = () => {
    if (allContractRows.length === 0) return;

    const headers = [
      String(i18next.t('table-header-hotel-id')),
      String(i18next.t('table-header-hotel-name')),
      String(i18next.t('table-header-zip')),
      String(i18next.t('table-header-state')),
      String(i18next.t('table-header-country')),
      String(i18next.t('table-header-hotel-groups')),
      String(i18next.t('filter-contract-types')),
      String(i18next.t('table-header-contract-valid-from')),
      String(i18next.t('table-header-contract-valid-to')),
      String(i18next.t('table-header-invoice-interval')),
      String(i18next.t('table-header-contract-amount')),
      String(i18next.t('table-header-show-in-lister')),
    ];

    const rows = allContractRows.map(({ hotel, contract }: any) => [
      hotel.refCode || '',
      hotel.name || '',
      hotel.businessZip || '',
      getSubdivisionName(hotel.businessState) || '',
      hotel.businessCountry || '',
      hotel.groups.map((g: any) => g.name).join('; ') || '',
      contract ? formatContractType(contract.type) : '-',
      contract ? formatDate(new Date(contract.validFrom)) : '-',
      contract ? formatDate(new Date(contract.validTo)) : '-',
      contract ? formatInvoiceInterval(contract.invoiceInterval) : '-',
      contract ? formatNumber(contract.amountCents / 100) : '-',
      hotel.showInLister ? i18next.t('filter-cancellation-yes') : i18next.t('filter-cancellation-no'),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row: any[]) =>
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `hotel-vereinbarungen-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
  };

  const hotels = contractsData?.reportHotelContractsList || [];
  const totalCount = countData?.reportHotelContractsListCount || 0;
  const hotelGroups = groupsData?.reportListHotelGroups || [];

  // Flatten all contracts with their hotel info for pagination
  // If NO_CONTRACT filter is active, show hotels without contracts
  const allContractRows = hotels.flatMap((hotel: any) => {
    if (hotel.contracts.length === 0) {
      // Hotel without contracts (NO_CONTRACT filter)
      return [{ hotel, contract: null }];
    }
    return hotel.contracts.map((contract: any) => ({
      hotel,
      contract,
    }));
  });

  // Paginate contracts
  const paginatedRows = allContractRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const agreementTypeOptions = [
    ...Object.keys(CONTRACT_TYPE_LABELS).map(value => ({
      value,
      label: String(
        i18next.t(`contracts-type-${value}`, {
          defaultValue: CONTRACT_TYPE_LABELS[value as keyof typeof CONTRACT_TYPE_LABELS],
        }),
      ),
    })),
    { value: 'NO_CONTRACT', label: String(i18next.t('contracts-agreement-type-none')) },
  ];

  return (
    <Box component="form" sx={{ width: '100%' }}>
      {/* Filters */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 2,
          mt: 2,
        }}
      >
        {/* Column 1 */}
        <FormControl fullWidth>
          <InputLabel>{i18next.t('filter-country-hotel')}</InputLabel>
          <Select
            value={filters.country || ''}
            label={i18next.t('filter-country-hotel')}
            onChange={(e) => handleFilterChange('country', e.target.value ? e.target.value as ECountryCode : undefined)}
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            {allCountriesSorted
              .filter(c => c !== null)
              .map((country: any) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Column 2 */}
        <FormControl fullWidth>
          <InputLabel>{i18next.t('filter-state')}</InputLabel>
          <Select
            value={filters.state || ''}
            label={i18next.t('filter-state')}
            onChange={(e) => handleFilterChange('state', e.target.value || undefined)}
            disabled={!filters.country}
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            {states.map((state) => (
              <MenuItem key={state.code} value={state.code}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Column 3 */}
        <FormControl fullWidth>
          <InputLabel>{i18next.t('filter-hotel-group')}</InputLabel>
          <Select
            multiple
            value={filters.groupIds || []}
            onChange={(e: SelectChangeEvent<number[]>) =>
              handleFilterChange('groupIds', e.target.value as number[])
            }
            input={<OutlinedInput label={i18next.t('filter-hotel-group')} />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as number[]).map((value) => (
                  <Chip key={value} label={hotelGroups.find((g: any) => g.id === value)?.name || ''} />
                ))}
              </Box>
            )}
          >
            {hotelGroups.map((group: any) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Column 4 */}
        <FormControl fullWidth>
          <InputLabel>{i18next.t('filter-agreement-type')}</InputLabel>
          <Select
            multiple
            value={filters.agreementTypes || []}
            input={<OutlinedInput label={i18next.t('filter-agreement-type')} />}
            onChange={(e: SelectChangeEvent<string[]>) => {
              const value = e.target.value as string[];
              // Selecting "All" clears any specific selections
              if (value.includes('')) {
                handleFilterChange('agreementTypes', []);
              } else {
                handleFilterChange('agreementTypes', value);
              }
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip
                    key={value}
                    label={agreementTypeOptions.find((o) => o.value === value)?.label || value}
                    size="small"
                  />
                ))}
              </Box>
            )}
          >
            <MenuItem value="">
              <Checkbox checked={(filters.agreementTypes || []).length === 0} />
              {i18next.t('filter-status-all')}
            </MenuItem>
            {agreementTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={(filters.agreementTypes || []).includes(option.value)} />
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Row 2 */}
        <SimpleDatePicker
          label={i18next.t('filter-contract-valid-from')}
          value={filters.validFrom ? new Date(filters.validFrom) : null}
          onChange={(date) => handleFilterChange('validFrom', date)}
        />
        <SimpleDatePicker
          label={i18next.t('filter-contract-valid-to')}
          value={filters.validTo ? new Date(filters.validTo) : null}
          onChange={(date) => handleFilterChange('validTo', date)}
        />

        <FormControl fullWidth>
          <InputLabel>{i18next.t('filter-invoice-interval')}</InputLabel>
          <Select
            value={filters.invoiceInterval || ''}
            label={i18next.t('filter-invoice-interval')}
            onChange={(e) => handleFilterChange('invoiceInterval', e.target.value || undefined)}
          >
            <MenuItem value="">{i18next.t('filter-invoice-interval-all')}</MenuItem>
            {getInvoiceIntervalOptions().map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{i18next.t('filter-show-in-lister')}</InputLabel>
          <Select
            value={filters.showInLister === undefined ? '' : filters.showInLister ? 'yes' : 'no'}
            label={i18next.t('filter-show-in-lister')}
            onChange={(e) =>
              handleFilterChange(
                'showInLister',
                e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined,
              )
            }
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            <MenuItem value="yes">{i18next.t('filter-show-in-lister-yes')}</MenuItem>
            <MenuItem value="no">{i18next.t('filter-show-in-lister-no')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateReport}
          disabled={contractsLoading}
        >
          {contractsLoading ? <CircularProgress size={20} /> : i18next.t('filter-generate-report')}
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          {i18next.t('filter-reset')}
        </Button>
      </Box>

      {/* Loading Indicator */}
      {contractsLoading && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{i18next.t('report-loading')}</Typography>
        </Box>
      )}

      {/* Results Table */}
      {!contractsLoading && allContractRows.length > 0 && (
        <>
          <TableContainer component={Paper} sx={{ mt: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{i18next.t('table-header-hotel-name')}</TableCell>
                  <TableCell>{i18next.t('table-header-zip')}</TableCell>
                  <TableCell>{i18next.t('table-header-state')}</TableCell>
                  <TableCell>{i18next.t('table-header-country')}</TableCell>
                  <TableCell>{i18next.t('table-header-hotel-groups')}</TableCell>
                  <TableCell>{i18next.t('filter-contract-types')}</TableCell>
                  <TableCell>{i18next.t('table-header-contract-valid-from')}</TableCell>
                  <TableCell>{i18next.t('table-header-contract-valid-to')}</TableCell>
                  <TableCell>{i18next.t('table-header-invoice-interval')}</TableCell>
                  <TableCell>{i18next.t('table-header-contract-amount')}</TableCell>
                  <TableCell>{i18next.t('table-header-show-in-lister')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map(({ hotel, contract }: any, idx: number) => (
                  <TableRow key={contract ? `${hotel.id}-${contract.id}` : `${hotel.id}-no-contract-${idx}`}>
                    <TableCell>
                      {hotel.id ? (
                        <Link to={`/settings/hotels/${hotel.id}`} style={{ textDecoration: 'none', color: '#AC2A6E' }}>
                          {hotel.name}
                        </Link>
                      ) : (
                        hotel.name
                      )}
                    </TableCell>
                    <TableCell>{hotel.businessZip}</TableCell>
                    <TableCell>{getSubdivisionName(hotel.businessState)}</TableCell>
                    <TableCell>{hotel.businessCountry}</TableCell>
                    <TableCell>
                      {hotel.groups.map((g: any) => g.name).join(', ')}
                    </TableCell>
                    <TableCell>{contract ? formatContractType(contract.type) : '-'}</TableCell>
                    <TableCell>{contract ? moment(contract.validFrom).format('DD.MM.YYYY') : '-'}</TableCell>
                    <TableCell>{contract ? moment(contract.validTo).format('DD.MM.YYYY') : '-'}</TableCell>
                    <TableCell>{contract ? formatInvoiceInterval(contract.invoiceInterval) : '-'}</TableCell>
                    <TableCell>{contract ? centsToEuro(contract.amountCents) : '-'}</TableCell>
                    <TableCell>{hotel.showInLister ? i18next.t('filter-cancellation-yes') : i18next.t('filter-cancellation-no')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100, 250]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            {...getMuiTablePaginationI18nProps()}
          />

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<GetAppIcon />}
              onClick={handleExportCSV}
              color="secondary"
              size="large"
            >
              {i18next.t('export-contracts-to-csv')}
            </Button>
          </Box>
        </>
      )}

      {/* No Results Message */}
      {!contractsLoading && allContractRows.length === 0 && countData && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography>{i18next.t('no-results')}</Typography>
        </Box>
      )}
    </Box>
  );
}
